import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { DeltaChip } from '../DeltaChip';
import { ReportCard, SectionLabel } from '../ReportCard';
import type { PerformanceReport } from './data/api';
import { compactCount, compactCurrency, count, formatKpi } from './data/format';

interface StatCardProps {
  label: string;
  value: string;
  caption: string;
  delta?: number | null;
}

function StatCard({ label, value, caption, delta }: StatCardProps): ReactNode {
  return (
    <Box data-test={`kpi-tile-${label.toLowerCase().replaceAll(' ', '-')}`} sx={{ minWidth: 0 }}>
      <ReportCard>
        <SectionLabel>{label}</SectionLabel>
        <Typography sx={{ fontSize: 40, fontWeight: 300, color: 'text.primary' }}>
          {value}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {delta !== undefined && delta !== null && <DeltaChip value={delta} />}
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{caption}</Typography>
        </Box>
      </ReportCard>
    </Box>
  );
}

export function KpiTiles({ report }: { report: PerformanceReport }): ReactNode {
  const { summary, kpi, currencySymbol, dateRange } = report;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(2, minmax(0, 1fr))',
          xl: 'repeat(4, minmax(0, 1fr))',
        },
        gap: 3,
      }}
    >
      <StatCard
        label="Total Spend"
        value={compactCurrency(summary.totalSpend, currencySymbol)}
        caption={`${dateRange[0]} – ${dateRange[1]}`}
      />
      <StatCard
        label={kpi.label}
        value={formatKpi(summary.kpiValue, kpi, currencySymbol)}
        caption="vs previous period"
        delta={summary.kpiImprovement}
      />
      <StatCard
        label="Impressions"
        value={compactCount(summary.impressions)}
        caption="total delivered"
      />
      <StatCard
        label="Active Creatives"
        value={count(summary.activeCreatives)}
        caption="currently running"
      />
    </Box>
  );
}
